/**
 * FikoSmartLine - Next-Gen Smart Line Drawing System
 *
 * Akıllı çizgi çizim sistemi
 * - Bezier eğrileri (quadratic, cubic, n-degree)
 * - Catmull-Rom splines
 * - B-Splines
 * - NURBS curves
 * - Animasyonlu çizgiler
 * - GPU-accelerated rendering
 * - Interaktif kontrol noktaları
 *
 * @author FikoYT Advanced CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { getCanvasResolutionVec2 } from '../../../FikoCanvasResolution';

// ============================================================================
// ENUMS & TYPES
// ============================================================================

/**
 * Çizgi tipi
 */
export enum LineType {
    // Temel
    STRAIGHT = 'STRAIGHT',
    POLYLINE = 'POLYLINE',

    // Bezier
    BEZIER_QUADRATIC = 'BEZIER_QUADRATIC',
    BEZIER_CUBIC = 'BEZIER_CUBIC',
    BEZIER_N_DEGREE = 'BEZIER_N_DEGREE',

    // Spline
    CATMULL_ROM = 'CATMULL_ROM',
    B_SPLINE = 'B_SPLINE',
    NURBS = 'NURBS',

    // Özel
    WAVE_SINE = 'WAVE_SINE',
    WAVE_COSINE = 'WAVE_COSINE',
    ZIGZAG = 'ZIGZAG',
    SPIRAL = 'SPIRAL',
    HELIX = 'HELIX',

    // Teknik
    LEADER = 'LEADER',
    DIMENSION = 'DIMENSION',
    EXTENSION = 'EXTENSION',
    CENTERLINE = 'CENTERLINE'
}

/**
 * Çizgi stili
 */
export enum LineStyleType {
    SOLID = 'SOLID',
    DASHED = 'DASHED',
    DOTTED = 'DOTTED',
    DASH_DOT = 'DASH_DOT',
    DASH_DOT_DOT = 'DASH_DOT_DOT',

    // Gradient
    GRADIENT_LINEAR = 'GRADIENT_LINEAR',
    GRADIENT_RAINBOW = 'GRADIENT_RAINBOW',

    // Animasyonlu
    ANIMATED_FLOW = 'ANIMATED_FLOW',
    ANIMATED_PULSE = 'ANIMATED_PULSE',
    ANIMATED_DASH = 'ANIMATED_DASH',

    // Efektli
    NEON_GLOW = 'NEON_GLOW',
    HOLOGRAPHIC = 'HOLOGRAPHIC',
    PLASMA = 'PLASMA',
    ELECTRIC = 'ELECTRIC',

    // Profesyonel
    CATIA_STANDARD = 'CATIA_STANDARD',
    SOLIDWORKS_STANDARD = 'SOLIDWORKS_STANDARD',
    AUTOCAD_STANDARD = 'AUTOCAD_STANDARD',

    // Teknik çizim
    CENTERLINE = 'CENTERLINE',
    PHANTOM = 'PHANTOM',
    CONSTRUCTION = 'CONSTRUCTION'
}

/**
 * Animasyon tipi
 */
export enum LineAnimationType {
    NONE = 'NONE',
    FLOW = 'FLOW',
    PULSE = 'PULSE',
    DASH_ANIMATE = 'DASH_ANIMATE',
    GLOW = 'GLOW',
    DRAW = 'DRAW',
    WAVE = 'WAVE',
    SHIMMER = 'SHIMMER',
    SPARK = 'SPARK',
    TRAIL = 'TRAIL'
}

/**
 * Çizgi kapağı (line cap)
 */
export enum LineCapType {
    BUTT = 'BUTT',
    ROUND = 'ROUND',
    SQUARE = 'SQUARE',
    ARROW = 'ARROW',
    DIAMOND = 'DIAMOND',
    CIRCLE = 'CIRCLE'
}

/**
 * Çizgi eki (line join)
 */
export enum LineJoinType {
    MITER = 'MITER',
    ROUND = 'ROUND',
    BEVEL = 'BEVEL'
}

/**
 * Etkileşim durumu
 */
export enum LineState {
    IDLE = 'IDLE',
    HOVER = 'HOVER',
    SELECTED = 'SELECTED',
    EDITING = 'EDITING',
    DRAGGING = 'DRAGGING',
    DISABLED = 'DISABLED'
}

/**
 * Kontrol noktası tipi
 */
export enum ControlPointType {
    ENDPOINT = 'ENDPOINT',
    CONTROL = 'CONTROL',
    KNOT = 'KNOT',
    TANGENT = 'TANGENT'
}

/**
 * Çizgi konfigürasyonu
 */
export interface SmartLineConfig {
    // Noktalar
    points: THREE.Vector3[];
    controlPoints?: THREE.Vector3[];

    // Tip ve stil
    type: LineType;
    style: LineStyleType;

    // Renk
    color: number;
    secondaryColor?: number;
    opacity: number;

    // Kalınlık
    lineWidth: number;
    lineCap: LineCapType;
    lineJoin: LineJoinType;

    // Animasyon
    animation: LineAnimationType;
    animationSpeed: number;
    animationIntensity: number;

    // Segment/resolution
    segments: number;
    tension?: number; // Catmull-Rom için

    // Efektler
    enableGlow: boolean;
    glowIntensity: number;
    glowColor?: number;

    // Kontrol noktaları gösterimi
    showControlPoints: boolean;
    controlPointSize: number;
    controlPointColor: number;

    // Arrow/cap
    startCap: LineCapType;
    endCap: LineCapType;
    arrowSize: number;
}

/**
 * Kontrol noktası
 */
export interface ControlPoint {
    position: THREE.Vector3;
    type: ControlPointType;
    weight?: number;
    tangent?: THREE.Vector3;
}

/**
 * Tema renkleri
 */
export interface LineThemeColors {
    primary: number;
    secondary: number;
    accent: number;
    glow: number;
    controlPoint: number;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const DEFAULT_LINE_CONFIG: SmartLineConfig = {
    points: [],
    type: LineType.STRAIGHT,
    style: LineStyleType.SOLID,

    color: 0x00ff88,
    opacity: 1,

    lineWidth: 2,
    lineCap: LineCapType.ROUND,
    lineJoin: LineJoinType.ROUND,

    animation: LineAnimationType.NONE,
    animationSpeed: 1,
    animationIntensity: 0.5,

    segments: 64,
    tension: 0.5,

    enableGlow: false,
    glowIntensity: 0.5,

    showControlPoints: false,
    controlPointSize: 0.05,
    controlPointColor: 0xff6600,

    startCap: LineCapType.ROUND,
    endCap: LineCapType.ROUND,
    arrowSize: 0.1
};

const LINE_THEMES: Record<string, LineThemeColors> = {
    NEON: {
        primary: 0x00ff88,
        secondary: 0x00ffff,
        accent: 0xff00ff,
        glow: 0x00ff88,
        controlPoint: 0xff6600
    },
    HOLOGRAPHIC: {
        primary: 0x88ccff,
        secondary: 0xff88cc,
        accent: 0xccff88,
        glow: 0xaaddff,
        controlPoint: 0xffaa00
    },
    BLUEPRINT: {
        primary: 0x4488ff,
        secondary: 0x2266dd,
        accent: 0x66aaff,
        glow: 0x88ccff,
        controlPoint: 0xffffff
    },
    MATRIX: {
        primary: 0x00ff00,
        secondary: 0x00cc00,
        accent: 0x00ff44,
        glow: 0x00ff00,
        controlPoint: 0xffff00
    },
    FIRE: {
        primary: 0xff4400,
        secondary: 0xff8800,
        accent: 0xffcc00,
        glow: 0xff6600,
        controlPoint: 0xffffff
    }
};

// ============================================================================
// SHADER DEFINITIONS
// ============================================================================

const SMART_LINE_VERTEX_SHADER = `
    attribute float progress;
    attribute vec3 color;

    varying float vProgress;
    varying vec3 vColor;
    varying vec2 vUv;

    void main() {
        vProgress = progress;
        vColor = color;
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const SMART_LINE_FRAGMENT_SHADER = `
    uniform vec3 baseColor;
    uniform vec3 secondaryColor;
    uniform float time;
    uniform float flowSpeed;
    uniform int animationType;
    uniform float opacity;
    uniform float drawProgress;

    varying float vProgress;
    varying vec3 vColor;
    varying vec2 vUv;

    void main() {
        vec3 finalColor = baseColor;
        float alpha = opacity;

        // Draw animation - progressive reveal
        if (animationType == 5) {
            if (vProgress > drawProgress) {
                discard;
            }
            alpha = opacity;
        }
        // Flow animation
        else if (animationType == 1) {
            float flow = fract(vProgress - time * flowSpeed);
            finalColor = mix(baseColor, secondaryColor, flow);
        }
        // Pulse animation
        else if (animationType == 2) {
            float pulse = sin(time * flowSpeed * 2.0 + vProgress * 10.0) * 0.5 + 0.5;
            finalColor = mix(baseColor, secondaryColor, pulse);
            alpha = opacity * (0.5 + pulse * 0.5);
        }
        // Dash animation
        else if (animationType == 3) {
            float dash = step(0.5, fract((vProgress - time * flowSpeed) * 10.0));
            alpha = opacity * dash;
        }
        // Glow animation
        else if (animationType == 4) {
            float glow = sin(time * flowSpeed) * 0.5 + 0.5;
            finalColor = mix(baseColor, secondaryColor, glow * 0.5);
            alpha = opacity * (0.7 + glow * 0.3);
        }
        // Wave animation
        else if (animationType == 6) {
            float wave = sin(vProgress * 20.0 + time * flowSpeed) * 0.5 + 0.5;
            finalColor = mix(baseColor, secondaryColor, wave);
        }
        // Shimmer animation
        else if (animationType == 7) {
            float shimmer = sin(vProgress * 50.0 + time * flowSpeed * 5.0) * 0.3 + 0.7;
            alpha = opacity * shimmer;
        }

        gl_FragColor = vec4(finalColor, alpha);
    }
`;

const GLOW_VERTEX_SHADER = `
    varying vec2 vUv;

    void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const GLOW_FRAGMENT_SHADER = `
    uniform vec3 glowColor;
    uniform float glowIntensity;
    uniform float time;

    varying vec2 vUv;

    void main() {
        float pulse = sin(time * 2.0) * 0.2 + 0.8;
        float glow = glowIntensity * pulse;

        float dist = abs(vUv.y - 0.5) * 2.0;
        float falloff = 1.0 - pow(dist, 0.5);

        vec3 finalColor = glowColor * glow;
        float alpha = falloff * glow;

        gl_FragColor = vec4(finalColor, alpha);
    }
`;

// ============================================================================
// MAIN CLASS
// ============================================================================

/**
 * FikoSmartLine - Akıllı Çizgi Çizim Sistemi
 */
export class FikoSmartLine extends THREE.Group {

    // Konfigürasyon
    private config: SmartLineConfig;
    private theme: LineThemeColors;

    // Bileşenler
    private mainLine: Line2 | THREE.Line | null = null;
    private glowMesh: THREE.Mesh | null = null;
    private controlPointMeshes: THREE.Mesh[] = [];
    private startCapMesh: THREE.Mesh | null = null;
    private endCapMesh: THREE.Mesh | null = null;
    private tangentLines: THREE.Line[] = [];

    // Eğri nesnesi
    private curve: THREE.Curve<THREE.Vector3> | null = null;
    private curvePoints: THREE.Vector3[] = [];

    // Animasyon
    private animationId: number = 0;
    private isAnimating: boolean = false;
    private animationStartTime: number = 0;
    private drawProgress: number = 1;
    private lineMaterial: THREE.ShaderMaterial | null = null;
    private glowMaterial: THREE.ShaderMaterial | null = null;

    // Durum
    private currentState: LineState = LineState.IDLE;
    private selectedControlPoint: number = -1;

    // Cache
    private static geometryCache: Map<string, THREE.BufferGeometry> = new Map();

    // Reusable objects
    private static readonly _tempVec3 = new THREE.Vector3();
    private static readonly _tempVec3_2 = new THREE.Vector3();

    // ========================================================================
    // CONSTRUCTOR
    // ========================================================================

    constructor(config: Partial<SmartLineConfig> = {}) {
        super();

        this.config = { ...DEFAULT_LINE_CONFIG, ...config };
        this.theme = LINE_THEMES.NEON;

        this.name = 'FikoSmartLine';
        this.userData.type = 'smart-line';
        this.userData.selectable = true;
        this.userData.editable = true;

        if (this.config.points.length >= 2) {
            this.buildLine();
        }

        if (this.config.animation !== LineAnimationType.NONE) {
            this.startAnimation();
        }
    }

    // ========================================================================
    // PUBLIC API
    // ========================================================================

    /**
     * Noktaları ayarla
     */
    public setPoints(points: THREE.Vector3[]): void {
        this.config.points = points.map(p => p.clone());
        this.rebuild();
    }

    /**
     * Nokta ekle
     */
    public addPoint(point: THREE.Vector3): void {
        this.config.points.push(point.clone());
        this.rebuild();
    }

    /**
     * Nokta güncelle
     */
    public updatePoint(index: number, point: THREE.Vector3): void {
        if (index >= 0 && index < this.config.points.length) {
            this.config.points[index].copy(point);
            this.rebuild();
        }
    }

    /**
     * Nokta sil
     */
    public removePoint(index: number): void {
        if (index >= 0 && index < this.config.points.length) {
            this.config.points.splice(index, 1);
            this.rebuild();
        }
    }

    /**
     * Kontrol noktalarını ayarla (Bezier için)
     */
    public setControlPoints(points: THREE.Vector3[]): void {
        this.config.controlPoints = points.map(p => p.clone());
        this.rebuild();
    }

    /**
     * Çizgi tipini değiştir
     */
    public setType(type: LineType): void {
        this.config.type = type;
        this.rebuild();
    }

    /**
     * Stili değiştir
     */
    public setStyle(style: LineStyleType): void {
        this.config.style = style;
        this.rebuild();
    }

    /**
     * Tema uygula
     */
    public applyTheme(themeName: string): void {
        if (LINE_THEMES[themeName]) {
            this.theme = LINE_THEMES[themeName];
            this.config.color = this.theme.primary;
            this.config.secondaryColor = this.theme.secondary;
            this.config.glowColor = this.theme.glow;
            this.config.controlPointColor = this.theme.controlPoint;
            this.rebuild();
        }
    }

    /**
     * Özel tema ayarla
     */
    public setCustomTheme(theme: LineThemeColors): void {
        this.theme = theme;
        this.config.color = theme.primary;
        this.config.secondaryColor = theme.secondary;
        this.config.glowColor = theme.glow;
        this.config.controlPointColor = theme.controlPoint;
        this.rebuild();
    }

    /**
     * Durum değiştir
     */
    public setState(state: LineState): void {
        if (this.currentState === state) return;
        this.currentState = state;
        this.applyStateVisuals();
    }

    /**
     * Çizgi uzunluğunu al
     */
    public getLength(): number {
        if (this.curve) {
            return this.curve.getLength();
        }

        // Polyline için manuel hesapla
        let length = 0;
        for (let i = 1; i < this.curvePoints.length; i++) {
            length += this.curvePoints[i].distanceTo(this.curvePoints[i - 1]);
        }
        return length;
    }

    /**
     * Belirli bir t değerindeki noktayı al
     */
    public getPointAt(t: number): THREE.Vector3 | null {
        if (this.curve) {
            return this.curve.getPointAt(t);
        }

        // Polyline için interpolasyon
        if (this.curvePoints.length < 2) return null;

        const totalLength = this.getLength();
        const targetLength = t * totalLength;

        let currentLength = 0;
        for (let i = 1; i < this.curvePoints.length; i++) {
            const segmentLength = this.curvePoints[i].distanceTo(this.curvePoints[i - 1]);

            if (currentLength + segmentLength >= targetLength) {
                const segmentT = (targetLength - currentLength) / segmentLength;
                return this.curvePoints[i - 1].clone().lerp(this.curvePoints[i], segmentT);
            }

            currentLength += segmentLength;
        }

        return this.curvePoints[this.curvePoints.length - 1].clone();
    }

    /**
     * Belirli bir t değerindeki teğet vektörü al
     */
    public getTangentAt(t: number): THREE.Vector3 | null {
        if (this.curve) {
            return this.curve.getTangentAt(t);
        }
        return null;
    }

    /**
     * Animasyonu başlat
     */
    public startAnimation(): void {
        if (this.isAnimating) return;

        this.isAnimating = true;
        this.animationStartTime = performance.now();

        if (this.config.animation === LineAnimationType.DRAW) {
            this.drawProgress = 0;
        }

        this.animate();
    }

    /**
     * Animasyonu durdur
     */
    public stopAnimation(): void {
        this.isAnimating = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = 0;
        }
    }

    /**
     * Draw animasyonunu resetle
     */
    public resetDrawAnimation(): void {
        this.drawProgress = 0;
        if (this.lineMaterial) {
            this.lineMaterial.uniforms.drawProgress.value = 0;
        }
    }

    /**
     * Glow efektini aç/kapat
     */
    public setGlow(enabled: boolean, intensity?: number): void {
        this.config.enableGlow = enabled;
        if (intensity !== undefined) {
            this.config.glowIntensity = intensity;
        }

        if (this.glowMesh) {
            this.glowMesh.visible = enabled;
            if (this.glowMaterial) {
                this.glowMaterial.uniforms.glowIntensity.value = this.config.glowIntensity;
            }
        }
    }

    /**
     * Kontrol noktalarını göster/gizle
     */
    public showControlPoints(show: boolean): void {
        this.config.showControlPoints = show;
        this.controlPointMeshes.forEach(mesh => {
            mesh.visible = show;
        });
        this.tangentLines.forEach(line => {
            line.visible = show;
        });
    }

    /**
     * Yeniden oluştur
     */
    public rebuild(): void {
        this.dispose();
        if (this.config.points.length >= 2) {
            this.buildLine();
        }

        if (this.config.animation !== LineAnimationType.NONE) {
            this.startAnimation();
        }
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        this.stopAnimation();

        if (this.mainLine) {
            if (this.mainLine.geometry) this.mainLine.geometry.dispose();
            if ((this.mainLine as any).material) {
                const mat = (this.mainLine as any).material;
                if (Array.isArray(mat)) {
                    mat.forEach(m => m.dispose());
                } else {
                    mat.dispose();
                }
            }
            this.remove(this.mainLine);
            this.mainLine = null;
        }

        if (this.glowMesh) {
            if (this.glowMesh.geometry) this.glowMesh.geometry.dispose();
            if (this.glowMesh.material) (this.glowMesh.material as THREE.Material).dispose();
            this.remove(this.glowMesh);
            this.glowMesh = null;
        }

        this.controlPointMeshes.forEach(mesh => {
            if (mesh.geometry) mesh.geometry.dispose();
            if (mesh.material) (mesh.material as THREE.Material).dispose();
            this.remove(mesh);
        });
        this.controlPointMeshes = [];

        this.tangentLines.forEach(line => {
            if (line.geometry) line.geometry.dispose();
            if (line.material) (line.material as THREE.Material).dispose();
            this.remove(line);
        });
        this.tangentLines = [];

        if (this.startCapMesh) {
            if (this.startCapMesh.geometry) this.startCapMesh.geometry.dispose();
            if (this.startCapMesh.material) (this.startCapMesh.material as THREE.Material).dispose();
            this.remove(this.startCapMesh);
            this.startCapMesh = null;
        }

        if (this.endCapMesh) {
            if (this.endCapMesh.geometry) this.endCapMesh.geometry.dispose();
            if (this.endCapMesh.material) (this.endCapMesh.material as THREE.Material).dispose();
            this.remove(this.endCapMesh);
            this.endCapMesh = null;
        }

        this.curve = null;
        this.curvePoints = [];
        this.lineMaterial = null;
        this.glowMaterial = null;
    }

    // ========================================================================
    // PRIVATE METHODS - BUILD
    // ========================================================================

    /**
     * Çizgiyi oluştur
     */
    private buildLine(): void {
        // Eğri oluştur
        this.createCurve();

        // Eğri noktalarını hesapla
        this.calculateCurvePoints();

        // Ana çizgi
        this.createMainLine();

        // Glow efekti
        if (this.config.enableGlow) {
            this.createGlowEffect();
        }

        // Kontrol noktaları
        if (this.config.showControlPoints) {
            this.createControlPointVisuals();
        }

        // Cap'ler (başlangıç/bitiş)
        this.createLineCaps();
    }

    /**
     * Eğri nesnesini oluştur
     */
    private createCurve(): void {
        const points = this.config.points;

        switch (this.config.type) {
            case LineType.STRAIGHT:
            case LineType.POLYLINE:
                // Polyline - eğri yok, direkt noktalar
                this.curve = null;
                break;

            case LineType.BEZIER_QUADRATIC:
                if (points.length >= 3) {
                    this.curve = new THREE.QuadraticBezierCurve3(
                        points[0],
                        this.config.controlPoints?.[0] || points[1],
                        points[points.length - 1]
                    );
                }
                break;

            case LineType.BEZIER_CUBIC:
                if (points.length >= 2) {
                    const cp1 = this.config.controlPoints?.[0] ||
                        points[0].clone().lerp(points[points.length - 1], 0.33);
                    const cp2 = this.config.controlPoints?.[1] ||
                        points[0].clone().lerp(points[points.length - 1], 0.66);

                    this.curve = new THREE.CubicBezierCurve3(
                        points[0],
                        cp1,
                        cp2,
                        points[points.length - 1]
                    );
                }
                break;

            case LineType.CATMULL_ROM:
                if (points.length >= 2) {
                    this.curve = new THREE.CatmullRomCurve3(
                        points,
                        false, // closed
                        'catmullrom',
                        this.config.tension || 0.5
                    );
                }
                break;

            case LineType.B_SPLINE:
                // B-Spline approximation using CatmullRom with modified tension
                if (points.length >= 2) {
                    this.curve = new THREE.CatmullRomCurve3(
                        points,
                        false,
                        'centripetal',
                        0.25
                    );
                }
                break;

            case LineType.SPIRAL:
                this.curve = this.createSpiralCurve();
                break;

            case LineType.HELIX:
                this.curve = this.createHelixCurve();
                break;

            case LineType.WAVE_SINE:
            case LineType.WAVE_COSINE:
            case LineType.ZIGZAG:
                // Wave patterns - hesaplanacak noktalarla
                this.curve = null;
                break;

            default:
                this.curve = null;
        }
    }

    /**
     * Spiral eğri oluştur
     */
    private createSpiralCurve(): THREE.Curve<THREE.Vector3> {
        const start = this.config.points[0];
        const end = this.config.points[this.config.points.length - 1];

        class SpiralCurve extends THREE.Curve<THREE.Vector3> {
            private start: THREE.Vector3;
            private end: THREE.Vector3;
            private turns: number;

            constructor(start: THREE.Vector3, end: THREE.Vector3, turns: number = 3) {
                super();
                this.start = start;
                this.end = end;
                this.turns = turns;
            }

            getPoint(t: number): THREE.Vector3 {
                const radius = (1 - t) * 0.2;
                const angle = t * Math.PI * 2 * this.turns;

                const basePoint = this.start.clone().lerp(this.end, t);

                return new THREE.Vector3(
                    basePoint.x + Math.cos(angle) * radius,
                    basePoint.y + Math.sin(angle) * radius,
                    basePoint.z
                );
            }
        }

        return new SpiralCurve(start, end);
    }

    /**
     * Helix eğri oluştur
     */
    private createHelixCurve(): THREE.Curve<THREE.Vector3> {
        const start = this.config.points[0];
        const end = this.config.points[this.config.points.length - 1];

        class HelixCurve extends THREE.Curve<THREE.Vector3> {
            private start: THREE.Vector3;
            private end: THREE.Vector3;
            private turns: number;
            private radius: number;

            constructor(start: THREE.Vector3, end: THREE.Vector3, turns: number = 3, radius: number = 0.1) {
                super();
                this.start = start;
                this.end = end;
                this.turns = turns;
                this.radius = radius;
            }

            getPoint(t: number): THREE.Vector3 {
                const angle = t * Math.PI * 2 * this.turns;

                const basePoint = this.start.clone().lerp(this.end, t);

                return new THREE.Vector3(
                    basePoint.x + Math.cos(angle) * this.radius,
                    basePoint.y + Math.sin(angle) * this.radius,
                    basePoint.z
                );
            }
        }

        return new HelixCurve(start, end);
    }

    /**
     * Eğri noktalarını hesapla
     */
    private calculateCurvePoints(): void {
        const segments = this.config.segments;

        if (this.curve) {
            this.curvePoints = this.curve.getPoints(segments);
        } else {
            // Wave patterns veya polyline
            switch (this.config.type) {
                case LineType.WAVE_SINE:
                    this.curvePoints = this.calculateWavePoints('sine');
                    break;
                case LineType.WAVE_COSINE:
                    this.curvePoints = this.calculateWavePoints('cosine');
                    break;
                case LineType.ZIGZAG:
                    this.curvePoints = this.calculateZigzagPoints();
                    break;
                default:
                    // Polyline
                    this.curvePoints = this.config.points.map(p => p.clone());
            }
        }
    }

    /**
     * Wave noktaları hesapla
     */
    private calculateWavePoints(type: 'sine' | 'cosine'): THREE.Vector3[] {
        const points: THREE.Vector3[] = [];
        const sourcePoints = this.config.points;

        if (sourcePoints.length < 2) return points;

        const segments = this.config.segments;
        const waveFrequency = 5;
        const waveAmplitude = 0.05;

        for (let i = 0; i <= segments; i++) {
            const t = i / segments;

            // Kaynak çizgi üzerinde interpolasyon
            const segmentIndex = Math.min(
                Math.floor(t * (sourcePoints.length - 1)),
                sourcePoints.length - 2
            );
            const segmentT = (t * (sourcePoints.length - 1)) - segmentIndex;

            const basePoint = sourcePoints[segmentIndex].clone()
                .lerp(sourcePoints[segmentIndex + 1], segmentT);

            // Yön vektörü (normal)
            const direction = sourcePoints[segmentIndex + 1].clone()
                .sub(sourcePoints[segmentIndex]).normalize();

            // Perpendicular vector
            const perpendicular = new THREE.Vector3(-direction.y, direction.x, 0);

            let offset = 0;
            if (type === 'sine') {
                offset = Math.sin(t * Math.PI * 2 * waveFrequency) * waveAmplitude;
            } else {
                offset = Math.cos(t * Math.PI * 2 * waveFrequency) * waveAmplitude;
            }

            points.push(new THREE.Vector3(
                basePoint.x + perpendicular.x * offset,
                basePoint.y + perpendicular.y * offset,
                basePoint.z + perpendicular.z * offset
            ));
        }

        return points;
    }

    /**
     * Zigzag noktaları hesapla
     */
    private calculateZigzagPoints(): THREE.Vector3[] {
        const points: THREE.Vector3[] = [];
        const sourcePoints = this.config.points;

        if (sourcePoints.length < 2) return points;

        const zigzagCount = 20;
        const amplitude = 0.05;

        for (let i = 0; i <= zigzagCount; i++) {
            const t = i / zigzagCount;

            const segmentIndex = Math.min(
                Math.floor(t * (sourcePoints.length - 1)),
                sourcePoints.length - 2
            );
            const segmentT = (t * (sourcePoints.length - 1)) - segmentIndex;

            const basePoint = sourcePoints[segmentIndex].clone()
                .lerp(sourcePoints[segmentIndex + 1], segmentT);

            const direction = sourcePoints[segmentIndex + 1].clone()
                .sub(sourcePoints[segmentIndex]).normalize();

            const perpendicular = new THREE.Vector3(-direction.y, direction.x, 0);

            const offset = (i % 2 === 0 ? 1 : -1) * amplitude;

            points.push(new THREE.Vector3(
                basePoint.x + perpendicular.x * offset,
                basePoint.y + perpendicular.y * offset,
                basePoint.z + perpendicular.z * offset
            ));
        }

        return points;
    }

    /**
     * Ana çizgiyi oluştur
     */
    private createMainLine(): void {
        if (this.curvePoints.length < 2) return;

        const isAnimated = this.config.animation !== LineAnimationType.NONE ||
            this.config.style === LineStyleType.ANIMATED_FLOW ||
            this.config.style === LineStyleType.ANIMATED_PULSE ||
            this.config.style === LineStyleType.ANIMATED_DASH;

        if (isAnimated || this.config.style === LineStyleType.GRADIENT_LINEAR ||
            this.config.style === LineStyleType.GRADIENT_RAINBOW) {
            this.createShaderLine();
        } else if (this.config.style === LineStyleType.DASHED ||
            this.config.style === LineStyleType.DOTTED ||
            this.config.style === LineStyleType.DASH_DOT ||
            this.config.style === LineStyleType.CENTERLINE) {
            this.createDashedLine();
        } else {
            this.createSolidLine();
        }
    }

    /**
     * Solid çizgi oluştur
     */
    private createSolidLine(): void {
        const positions: number[] = [];
        this.curvePoints.forEach(p => positions.push(p.x, p.y, p.z));

        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color: this.config.color,
            linewidth: this.config.lineWidth * 0.001,
            opacity: this.config.opacity,
            transparent: this.config.opacity < 1,
            resolution: getCanvasResolutionVec2()
        });

        this.mainLine = new Line2(geometry, material);
        this.mainLine.computeLineDistances();
        this.add(this.mainLine);
    }

    /**
     * Dashed çizgi oluştur
     */
    private createDashedLine(): void {
        const geometry = new THREE.BufferGeometry().setFromPoints(this.curvePoints);

        let dashSize = 0.1;
        let gapSize = 0.05;

        switch (this.config.style) {
            case LineStyleType.DOTTED:
                dashSize = 0.02;
                gapSize = 0.04;
                break;
            case LineStyleType.DASH_DOT:
                dashSize = 0.1;
                gapSize = 0.03;
                break;
            case LineStyleType.CENTERLINE:
                dashSize = 0.15;
                gapSize = 0.02;
                break;
        }

        const material = new THREE.LineDashedMaterial({
            color: this.config.color,
            linewidth: this.config.lineWidth,
            opacity: this.config.opacity,
            transparent: this.config.opacity < 1,
            dashSize: dashSize,
            gapSize: gapSize
        });

        this.mainLine = new THREE.Line(geometry, material);
        this.mainLine.computeLineDistances();
        this.add(this.mainLine);
    }

    /**
     * Shader-based çizgi oluştur
     */
    private createShaderLine(): void {
        const positions: number[] = [];
        const progress: number[] = [];
        const colors: number[] = [];

        const color1 = new THREE.Color(this.config.color);
        const color2 = new THREE.Color(this.config.secondaryColor || this.theme.secondary);

        this.curvePoints.forEach((p, i) => {
            positions.push(p.x, p.y, p.z);

            const t = i / (this.curvePoints.length - 1);
            progress.push(t);

            // Gradient colors
            if (this.config.style === LineStyleType.GRADIENT_RAINBOW) {
                const rainbowColor = new THREE.Color().setHSL(t, 1, 0.5);
                colors.push(rainbowColor.r, rainbowColor.g, rainbowColor.b);
            } else {
                const gradientColor = color1.clone().lerp(color2, t);
                colors.push(gradientColor.r, gradientColor.g, gradientColor.b);
            }
        });

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('progress', new THREE.Float32BufferAttribute(progress, 1));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

        // Animation type mapping
        let animationType = 0;
        switch (this.config.animation) {
            case LineAnimationType.FLOW: animationType = 1; break;
            case LineAnimationType.PULSE: animationType = 2; break;
            case LineAnimationType.DASH_ANIMATE: animationType = 3; break;
            case LineAnimationType.GLOW: animationType = 4; break;
            case LineAnimationType.DRAW: animationType = 5; break;
            case LineAnimationType.WAVE: animationType = 6; break;
            case LineAnimationType.SHIMMER: animationType = 7; break;
        }

        this.lineMaterial = new THREE.ShaderMaterial({
            uniforms: {
                baseColor: { value: color1 },
                secondaryColor: { value: color2 },
                time: { value: 0 },
                flowSpeed: { value: this.config.animationSpeed },
                animationType: { value: animationType },
                opacity: { value: this.config.opacity },
                drawProgress: { value: this.drawProgress }
            },
            vertexShader: SMART_LINE_VERTEX_SHADER,
            fragmentShader: SMART_LINE_FRAGMENT_SHADER,
            transparent: true
        });

        this.mainLine = new THREE.Line(geometry, this.lineMaterial);
        this.add(this.mainLine);
    }

    /**
     * Glow efekti oluştur
     */
    private createGlowEffect(): void {
        if (this.curvePoints.length < 2) return;

        const curve = new THREE.CatmullRomCurve3(this.curvePoints);
        const tubeGeometry = new THREE.TubeGeometry(
            curve,
            this.config.segments,
            this.config.lineWidth * 0.005,
            8,
            false
        );

        this.glowMaterial = new THREE.ShaderMaterial({
            uniforms: {
                glowColor: { value: new THREE.Color(this.config.glowColor || this.theme.glow) },
                glowIntensity: { value: this.config.glowIntensity },
                time: { value: 0 }
            },
            vertexShader: GLOW_VERTEX_SHADER,
            fragmentShader: GLOW_FRAGMENT_SHADER,
            transparent: true,
            blending: THREE.AdditiveBlending,
            side: THREE.DoubleSide,
            depthWrite: false
        });

        this.glowMesh = new THREE.Mesh(tubeGeometry, this.glowMaterial);
        this.add(this.glowMesh);
    }

    /**
     * Kontrol noktası görselleri oluştur
     */
    private createControlPointVisuals(): void {
        const sphereGeometry = new THREE.SphereGeometry(this.config.controlPointSize, 16, 16);
        const material = new THREE.MeshBasicMaterial({
            color: this.config.controlPointColor,
            opacity: 0.8,
            transparent: true
        });

        // Ana noktalar
        this.config.points.forEach((point, index) => {
            const mesh = new THREE.Mesh(sphereGeometry.clone(), material.clone());
            mesh.position.copy(point);
            mesh.userData.type = 'control-point';
            mesh.userData.index = index;
            mesh.userData.pointType = ControlPointType.ENDPOINT;
            this.controlPointMeshes.push(mesh);
            this.add(mesh);
        });

        // Bezier kontrol noktaları
        if (this.config.controlPoints) {
            const controlMaterial = new THREE.MeshBasicMaterial({
                color: this.theme.accent,
                opacity: 0.6,
                transparent: true
            });

            this.config.controlPoints.forEach((point, index) => {
                const mesh = new THREE.Mesh(sphereGeometry.clone(), controlMaterial.clone());
                mesh.position.copy(point);
                mesh.userData.type = 'control-point';
                mesh.userData.index = index;
                mesh.userData.pointType = ControlPointType.CONTROL;
                this.controlPointMeshes.push(mesh);
                this.add(mesh);

                // Tangent lines (kontrol noktasından ana noktaya)
                if (index < this.config.points.length) {
                    const tangentGeometry = new THREE.BufferGeometry().setFromPoints([
                        this.config.points[Math.min(index, this.config.points.length - 1)],
                        point
                    ]);
                    const tangentMaterial = new THREE.LineDashedMaterial({
                        color: this.theme.accent,
                        opacity: 0.4,
                        transparent: true,
                        dashSize: 0.05,
                        gapSize: 0.02
                    });
                    const tangentLine = new THREE.Line(tangentGeometry, tangentMaterial);
                    tangentLine.computeLineDistances();
                    this.tangentLines.push(tangentLine);
                    this.add(tangentLine);
                }
            });
        }
    }

    /**
     * Çizgi cap'lerini oluştur
     */
    private createLineCaps(): void {
        if (this.curvePoints.length < 2) return;

        // Start cap
        if (this.config.startCap === LineCapType.ARROW) {
            this.startCapMesh = this.createArrowCap(
                this.curvePoints[0],
                this.curvePoints[1],
                true
            );
            if (this.startCapMesh) this.add(this.startCapMesh);
        }

        // End cap
        if (this.config.endCap === LineCapType.ARROW) {
            this.endCapMesh = this.createArrowCap(
                this.curvePoints[this.curvePoints.length - 1],
                this.curvePoints[this.curvePoints.length - 2],
                false
            );
            if (this.endCapMesh) this.add(this.endCapMesh);
        }
    }

    /**
     * Ok cap oluştur
     */
    private createArrowCap(position: THREE.Vector3, referencePoint: THREE.Vector3, isStart: boolean): THREE.Mesh {
        const size = this.config.arrowSize;

        const shape = new THREE.Shape();
        shape.moveTo(0, 0);
        shape.lineTo(-size * 2, -size * 0.6);
        shape.lineTo(-size * 1.5, 0);
        shape.lineTo(-size * 2, size * 0.6);
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);
        const material = new THREE.MeshBasicMaterial({
            color: this.config.color,
            side: THREE.DoubleSide,
            opacity: this.config.opacity,
            transparent: this.config.opacity < 1
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.copy(position);

        // Yönlendirme
        const direction = position.clone().sub(referencePoint).normalize();
        const angle = Math.atan2(direction.y, direction.x);
        mesh.rotation.z = isStart ? angle + Math.PI : angle;

        return mesh;
    }

    // ========================================================================
    // PRIVATE METHODS - ANIMATION
    // ========================================================================

    /**
     * Animasyon döngüsü
     */
    private animate = (): void => {
        if (!this.isAnimating) return;

        const elapsed = (performance.now() - this.animationStartTime) / 1000;

        // Shader uniforms güncelle
        if (this.lineMaterial) {
            this.lineMaterial.uniforms.time.value = elapsed;

            // Draw animation progress
            if (this.config.animation === LineAnimationType.DRAW) {
                this.drawProgress = Math.min(1, this.drawProgress + 0.01 * this.config.animationSpeed);
                this.lineMaterial.uniforms.drawProgress.value = this.drawProgress;
            }
        }

        if (this.glowMaterial) {
            this.glowMaterial.uniforms.time.value = elapsed;
        }

        // Özel animasyonlar
        this.updateCustomAnimations(elapsed);

        this.animationId = requestAnimationFrame(this.animate);
    };

    /**
     * Özel animasyonları güncelle
     */
    private updateCustomAnimations(elapsed: number): void {
        switch (this.config.animation) {
            case LineAnimationType.TRAIL:
                // Trail efekti - pozisyon bazlı parçacıklar
                break;

            case LineAnimationType.SPARK:
                // Random parlaklık
                if (Math.random() < 0.1 && this.glowMaterial) {
                    this.glowMaterial.uniforms.glowIntensity.value =
                        this.config.glowIntensity * (1 + Math.random() * this.config.animationIntensity);

                    setTimeout(() => {
                        if (this.glowMaterial) {
                            this.glowMaterial.uniforms.glowIntensity.value = this.config.glowIntensity;
                        }
                    }, 50);
                }
                break;
        }
    }

    // ========================================================================
    // PRIVATE METHODS - STATE
    // ========================================================================

    /**
     * Durum görsellerini uygula
     */
    private applyStateVisuals(): void {
        switch (this.currentState) {
            case LineState.HOVER:
                this.setGlow(true, this.config.glowIntensity * 1.5);
                break;

            case LineState.SELECTED:
                this.setGlow(true, this.config.glowIntensity * 2);
                this.showControlPoints(true);
                break;

            case LineState.EDITING:
                this.setGlow(true, this.config.glowIntensity);
                this.showControlPoints(true);
                break;

            case LineState.DRAGGING:
                this.setGlow(true, this.config.glowIntensity);
                break;

            case LineState.DISABLED:
                this.setGlow(false);
                if (this.mainLine && (this.mainLine as any).material) {
                    ((this.mainLine as any).material as any).opacity = 0.3;
                }
                break;

            case LineState.IDLE:
            default:
                this.setGlow(this.config.enableGlow, this.config.glowIntensity);
                if (!this.config.showControlPoints) {
                    this.showControlPoints(false);
                }
                if (this.mainLine && (this.mainLine as any).material) {
                    ((this.mainLine as any).material as any).opacity = this.config.opacity;
                }
                break;
        }
    }

    // ========================================================================
    // STATIC FACTORY METHODS
    // ========================================================================

    /**
     * İki nokta arası düz çizgi
     */
    public static createStraightLine(
        start: THREE.Vector3,
        end: THREE.Vector3,
        config: Partial<SmartLineConfig> = {}
    ): FikoSmartLine {
        return new FikoSmartLine({
            points: [start, end],
            type: LineType.STRAIGHT,
            ...config
        });
    }

    /**
     * Bezier eğrisi
     */
    public static createBezierCurve(
        start: THREE.Vector3,
        end: THREE.Vector3,
        controlPoint1: THREE.Vector3,
        controlPoint2?: THREE.Vector3,
        config: Partial<SmartLineConfig> = {}
    ): FikoSmartLine {
        const type = controlPoint2 ? LineType.BEZIER_CUBIC : LineType.BEZIER_QUADRATIC;
        const controlPoints = controlPoint2 ? [controlPoint1, controlPoint2] : [controlPoint1];

        return new FikoSmartLine({
            points: [start, end],
            controlPoints,
            type,
            ...config
        });
    }

    /**
     * Spline eğrisi
     */
    public static createSpline(
        points: THREE.Vector3[],
        tension: number = 0.5,
        config: Partial<SmartLineConfig> = {}
    ): FikoSmartLine {
        return new FikoSmartLine({
            points,
            type: LineType.CATMULL_ROM,
            tension,
            ...config
        });
    }

    /**
     * Leader line (ölçü çizgisi)
     */
    public static createLeaderLine(
        start: THREE.Vector3,
        end: THREE.Vector3,
        labelPosition?: THREE.Vector3,
        config: Partial<SmartLineConfig> = {}
    ): FikoSmartLine {
        const points = labelPosition
            ? [start, end, labelPosition]
            : [start, end];

        return new FikoSmartLine({
            points,
            type: LineType.LEADER,
            style: LineStyleType.SOLID,
            endCap: LineCapType.ARROW,
            ...config
        });
    }

    /**
     * Preset tema ile oluştur
     */
    public static createWithTheme(
        themeName: string,
        config: Partial<SmartLineConfig> = {}
    ): FikoSmartLine {
        const line = new FikoSmartLine(config);
        line.applyTheme(themeName);
        return line;
    }
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
    LINE_THEMES,
    DEFAULT_LINE_CONFIG
};
